import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react'
export type NotificationType =
  | 'agent_interaction' // Agent needs user input
  | 'blocker' // Blocker detected
  | 'sprint_alert' // Sprint milestone
  | 'issue_update' // Issue status change
  | 'deploy' // Deploy event
  | 'pr' // Pull request
  | 'integration' // Integration event
  | 'success' // Generic success
  | 'warning' // Generic warning
  | 'system' // System/debug notifications

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  timestamp: number
  read: boolean
  action?: {
    label: string
    onClick: () => void
  }
  source?: string // "Jira", "GitHub", "Agent", etc.
}
interface NotificationsContextType {
  notifications: Notification[]
  unreadCount: number
  push: (n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => string
  markRead: (id: string) => void
  markAllRead: () => void
  remove: (id: string) => void
  clearAll: () => void
}
const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  unreadCount: 0,
  push: () => '',
  markRead: () => {},
  markAllRead: () => {},
  remove: () => {},
  clearAll: () => {},
})
const INITIAL_NOTIFICATIONS: Notification[] = [
  {
    id: 'init-1',
    type: 'blocker',
    title: 'Bloqueador detectado',
    message: 'VSM-101 sin avance por 2 días. El agente recomienda revisión.',
    timestamp: Date.now() - 420000,
    read: false,
    source: 'Jira',
  },
  {
    id: 'init-2',
    type: 'agent_interaction',
    title: 'El agente necesita tu input',
    message: '¿Confirmás mover VSM-103 a In Progress y asignarlo a Carlos?',
    timestamp: Date.now() - 180000,
    read: false,
    source: 'Agent',
    action: {
      label: 'Ir al chat',
      onClick: () => {},
    },
  },
  {
    id: 'init-3',
    type: 'sprint_alert',
    title: 'Sprint al 65%',
    message: 'Sprint 24 superó el punto medio con buen ritmo.',
    timestamp: Date.now() - 3300000,
    read: true,
    source: 'Agent',
  },
  {
    id: 'init-4',
    type: 'deploy',
    title: 'Deploy exitoso',
    message: 'v0.8.2 desplegado en staging (us-east-1).',
    timestamp: Date.now() - 5400000,
    read: true,
    source: 'GitHub',
  },
]
export function NotificationsProvider({
  children,
  onGoToChat,
  dataMode,
}: {
  children: React.ReactNode
  onGoToChat?: () => void
  dataMode?: 'mock' | 'live'
}) {
  const [notifications, setNotifications] = useState<Notification[]>(() =>
    dataMode === 'live'
      ? []
      : INITIAL_NOTIFICATIONS.map((n) => ({
          ...n,
          action: n.action
            ? {
                ...n.action,
                onClick: onGoToChat || (() => {}),
              }
            : undefined,
        })),
  )

  // Clear mock notifications when switching to live, restore when switching to mock
  const prevDataModeRef = useRef(dataMode)
  useEffect(() => {
    if (prevDataModeRef.current === dataMode) return
    prevDataModeRef.current = dataMode
    if (dataMode === 'live') {
      setNotifications([])
    } else {
      setNotifications(
        INITIAL_NOTIFICATIONS.map((n) => ({
          ...n,
          action: n.action
            ? {
                ...n.action,
                onClick: onGoToChat || (() => {}),
              }
            : undefined,
        })),
      )
    }
  }, [dataMode, onGoToChat])
  const push = useCallback(
    (n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
      const id = crypto.randomUUID()
      const newNotif: Notification = {
        ...n,
        id,
        timestamp: Date.now(),
        read: false,
      }
      setNotifications((prev) => [newNotif, ...prev])
      return id
    },
    [],
  )
  const markRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id
          ? {
              ...n,
              read: true,
            }
          : n,
      ),
    )
  }, [])
  const markAllRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        read: true,
      })),
    )
  }, [])
  const remove = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])
  const clearAll = useCallback(() => setNotifications([]), [])
  const unreadCount = notifications.filter((n) => !n.read).length
  return React.createElement(
    NotificationsContext.Provider,
    {
      value: {
        notifications,
        unreadCount,
        push,
        markRead,
        markAllRead,
        remove,
        clearAll,
      },
    },
    children,
  )
}
export function useNotifications() {
  return useContext(NotificationsContext)
}
